import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  TrendingUp, TrendingDown, Target, Award, BarChart3,
  CheckCircle, Clock, AlertTriangle, Loader2, Sparkles,
  Calendar, Minus, ChevronRight, RefreshCw, Send,
  MessageCircle, X, Zap, Star, ArrowUpRight, BookOpen, Heart, Trophy
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import api from '../api';

const ScoreRing = ({ score, size = 120, label }) => {
  const radius = (size - 16) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = score ? (score / 100) * circumference : 0;
  const color = score >= 70 ? '#059669' : score >= 50 ? '#2563eb' : score >= 30 ? '#d97706' : '#dc2626';

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#e2e8f0" strokeWidth="8" />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={color} strokeWidth="8"
          strokeDasharray={circumference} strokeDashoffset={circumference - progress}
          strokeLinecap="round" className="transition-all duration-1000 ease-out" />
      </svg>
      <div className="absolute flex flex-col items-center justify-center" style={{width: size, height: size}}>
        <span className="text-3xl font-bold" style={{color}}>{score ?? '--'}</span>
        <span className="text-xs text-slate-500">/100</span>
      </div>
      {label && <p className="text-xs text-slate-500 mt-1">{label}</p>}
    </div>
  );
};

const TrendBadge = ({ trend }) => {
  if (trend === 'up') return <Badge className="bg-emerald-100 text-emerald-700 gap-1"><TrendingUp className="h-3 w-3" /> Up</Badge>;
  if (trend === 'down') return <Badge className="bg-red-100 text-red-700 gap-1"><TrendingDown className="h-3 w-3" /> Down</Badge>;
  return <Badge className="bg-slate-100 text-slate-600 gap-1"><Minus className="h-3 w-3" /> Stable</Badge>;
};

const MyPerformanceDashboard = ({ user }) => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tips, setTips] = useState(null);
  const [tipsLoading, setTipsLoading] = useState(false);
  const [kudosStats, setKudosStats] = useState(null);
  const [milestones, setMilestones] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'ai', text: 'Hi! I\'m your AI performance assistant. Ask me about your scores, goals, or how to improve.' }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const messagesEndRef = React.useRef(null);

  useEffect(() => { fetchDashboard(); fetchKudosStats(); fetchMilestones(); }, []);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await api.get('/performance/ai/my-dashboard');
      setDashboard(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error('No employee profile linked to your account');
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally { setLoading(false); }
  };

  const fetchTips = async () => {
    setTipsLoading(true);
    try {
      const res = await api.post('/performance/ai/my-tips');
      setTips(res.data.tips);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to generate tips'); }
    finally { setTipsLoading(false); }
  };

  const fetchKudosStats = async () => {
    try {
      const res = await api.get('/kudos/my-stats');
      setKudosStats(res.data);
    } catch { /* user may not have employee profile */ }
  };

  const fetchMilestones = async () => {
    try {
      const res = await api.get('/kudos/milestones/my');
      setMilestones(res.data);
    } catch { /* silent */ }
  };

  const sendChat = async () => {
    if (!chatInput.trim() || chatSending) return;
    const msg = chatInput.trim();
    setChatInput('');
    setMessages(prev => [...prev, { role: 'user', text: msg }]);
    setChatSending(true);
    try {
      const res = await api.post('/performance/ai/chat', { message: msg });
      setMessages(prev => [...prev, { role: 'ai', text: res.data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: 'ai', text: 'Sorry, something went wrong. Please try again.' }]);
    } finally { setChatSending(false); }
  };

  const getStatusColor = (s) => ({
    'completed': 'bg-emerald-100 text-emerald-700', 'on_track': 'bg-blue-100 text-blue-700',
    'at_risk': 'bg-amber-100 text-amber-700', 'behind': 'bg-red-100 text-red-700'
  }[s] || 'bg-slate-100 text-slate-700');

  const getTierColor = (t) => ({
    'Exceptional': 'bg-amber-100 text-amber-800 border-amber-300',
    'High Performer': 'bg-emerald-100 text-emerald-800 border-emerald-300',
    'Meets Expectations': 'bg-blue-100 text-blue-800 border-blue-300',
    'Needs Improvement': 'bg-orange-100 text-orange-800 border-orange-300',
    'Underperforming': 'bg-red-100 text-red-800 border-red-300'
  }[t] || 'bg-slate-100 text-slate-600');

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      </Layout>
    );
  }

  if (!dashboard) {
    return (
      <Layout user={user}>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <AlertTriangle className="h-12 w-12 text-amber-500 mb-4" />
          <h2 className="text-xl font-semibold mb-2">No Employee Profile Found</h2>
          <p className="text-slate-500">Your account isn't linked to an employee record. Contact HR for assistance.</p>
        </div>
      </Layout>
    );
  }

  const { employee, attendance, performance, reviews, goals, monthly_trends } = dashboard;
  const perf = performance;

  return (
    <Layout user={user}>
      <div className="space-y-6" data-testid="my-performance-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">My Performance</h1>
            <p className="text-sm text-slate-500">{employee.designation} &middot; {employee.department || 'No Department'}</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchDashboard} data-testid="refresh-dashboard-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>

        {/* Top Row: Score + Attendance + Goals Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Performance Score Card */}
          <Card className="border-l-4 border-l-indigo-500" data-testid="performance-score-card">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div className="relative">
                  <ScoreRing score={perf.overall_score} />
                </div>
                <div className="text-right space-y-2">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Performance</p>
                  {perf.performance_tier && (
                    <Badge className={`${getTierColor(perf.performance_tier)} border`}>{perf.performance_tier}</Badge>
                  )}
                  {perf.promotion_readiness && (
                    <div>
                      <p className="text-xs text-slate-400 mt-2">Promotion</p>
                      <Badge variant="outline" className="text-xs">{perf.promotion_readiness}</Badge>
                    </div>
                  )}
                  {!perf.overall_score && <p className="text-xs text-slate-400 mt-2">No AI analysis yet</p>}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Attendance Card */}
          <Card className="border-l-4 border-l-emerald-500" data-testid="attendance-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">Attendance (90 Days)</p>
                <Calendar className="h-4 w-4 text-slate-400" />
              </div>
              <p className="text-3xl font-bold text-emerald-600">{attendance.attendance_rate}%</p>
              <div className="grid grid-cols-3 gap-2 mt-3">
                <div className="text-center p-2 bg-emerald-50 rounded">
                  <p className="text-lg font-semibold text-emerald-700">{attendance.present}</p>
                  <p className="text-[10px] text-slate-500">Present</p>
                </div>
                <div className="text-center p-2 bg-red-50 rounded">
                  <p className="text-lg font-semibold text-red-600">{attendance.absent}</p>
                  <p className="text-[10px] text-slate-500">Absent</p>
                </div>
                <div className="text-center p-2 bg-amber-50 rounded">
                  <p className="text-lg font-semibold text-amber-600">{attendance.late}</p>
                  <p className="text-[10px] text-slate-500">Late</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Goals Summary Card */}
          <Card className="border-l-4 border-l-violet-500" data-testid="goals-summary-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">My Goals</p>
                <Target className="h-4 w-4 text-slate-400" />
              </div>
              {goals.total > 0 ? (
                <>
                  <p className="text-3xl font-bold text-violet-600">{goals.completed}<span className="text-base text-slate-400 font-normal">/{goals.total}</span></p>
                  <p className="text-xs text-slate-500 mb-3">completed</p>
                  <div className="flex gap-2">
                    <Badge className="bg-blue-100 text-blue-700">{goals.on_track} On Track</Badge>
                    {goals.at_risk > 0 && <Badge className="bg-amber-100 text-amber-700">{goals.at_risk} At Risk</Badge>}
                  </div>
                </>
              ) : (
                <div className="text-center py-3">
                  <Target className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                  <p className="text-sm text-slate-400">No goals set yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Middle Row: Strengths & Improvements + Monthly Trends */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Strengths & Areas */}
          <Card data-testid="strengths-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><Star className="h-4 w-4 text-amber-500" /> Strengths & Growth Areas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {perf.strengths.length > 0 ? (
                <div>
                  <p className="text-xs font-medium text-emerald-600 uppercase mb-2">Strengths</p>
                  {perf.strengths.map((s, i) => (
                    <div key={i} className="flex items-start gap-2 mb-2">
                      <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-slate-700">{s}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400">Run an AI analysis to see your strengths</p>
              )}
              {perf.areas_of_improvement.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-amber-600 uppercase mb-2">Areas to Improve</p>
                  {perf.areas_of_improvement.map((a, i) => (
                    <div key={i} className="flex items-start gap-2 mb-2">
                      <ArrowUpRight className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-slate-700">{a}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Monthly Trends */}
          <Card data-testid="monthly-trends-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="h-4 w-4 text-indigo-500" /> Monthly Trend</CardTitle>
            </CardHeader>
            <CardContent>
              {monthly_trends.length > 0 ? (
                <div className="space-y-3">
                  {monthly_trends.map((t, i) => (
                    <div key={i} className="flex items-center justify-between p-2 border rounded-lg hover:bg-slate-50">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium w-20">{t.month}</span>
                        <TrendBadge trend={t.trend} />
                      </div>
                      <div className="flex items-center gap-3">
                        <Progress value={t.score} className="w-24 h-2" />
                        <span className={`text-sm font-bold ${t.score >= 70 ? 'text-emerald-600' : t.score >= 50 ? 'text-blue-600' : 'text-amber-600'}`}>{t.score}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <BarChart3 className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                  <p className="text-sm text-slate-400">No monthly data yet</p>
                  <p className="text-xs text-slate-400">Monthly snapshots will appear here</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Goals Detail */}
        {goals.items.length > 0 && (
          <Card data-testid="goals-detail-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><Target className="h-4 w-4 text-violet-500" /> Goal Progress</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {goals.items.map(goal => (
                  <div key={goal.id} className="p-3 border rounded-lg hover:shadow-sm transition-shadow">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={getStatusColor(goal.status)}>{goal.status?.replace('_', ' ')}</Badge>
                      <Badge variant="outline" className="text-[10px]">{goal.type}</Badge>
                      {goal.priority === 'high' || goal.priority === 'critical' ? (
                        <Badge variant="outline" className="border-red-300 text-red-600 text-[10px]">{goal.priority}</Badge>
                      ) : null}
                    </div>
                    <p className="font-medium text-sm mb-1">{goal.title}</p>
                    {goal.description && <p className="text-xs text-slate-500 mb-2">{goal.description}</p>}
                    <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                      <span>{goal.current_value ?? 0} / {goal.target_value} {goal.target_unit}</span>
                      <span className="font-medium">{goal.progress}%</span>
                    </div>
                    <Progress value={goal.progress || 0} className="h-2" />
                    {goal.due_date && <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-1"><Clock className="h-3 w-3" /> Due: {goal.due_date}</p>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reviews History */}
        {reviews.length > 0 && (
          <Card data-testid="reviews-history-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><Award className="h-4 w-4 text-amber-500" /> Review History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {reviews.map((r, i) => (
                  <div key={i} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        r.status === 'completed' ? 'bg-emerald-100' : r.status === 'submitted' ? 'bg-blue-100' : 'bg-slate-100'
                      }`}>
                        {r.status === 'completed' ? <CheckCircle className="h-5 w-5 text-emerald-600" /> :
                         r.status === 'submitted' ? <Clock className="h-5 w-5 text-blue-600" /> :
                         <Clock className="h-5 w-5 text-slate-400" />}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{r.cycle_name || 'Review Cycle'}</p>
                        <p className="text-xs text-slate-500">{r.kras_count} KRAs &middot; {r.status}</p>
                      </div>
                    </div>
                    {r.final_rating && (
                      <div className="text-right">
                        <p className="text-lg font-bold text-indigo-600">{r.final_rating}/10</p>
                        <p className="text-[10px] text-slate-400">Rating</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Kudos Recognition */}
        <Card className="border-l-4 border-l-pink-500" data-testid="kudos-summary-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2"><Heart className="h-4 w-4 text-pink-500" /> My Kudos</CardTitle>
              <Button variant="outline" size="sm" onClick={() => navigate('/kudos')} data-testid="view-kudos-wall-btn">
                View Wall <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {kudosStats ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-pink-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-pink-600">{kudosStats.received_count}</p>
                    <p className="text-xs text-slate-500">Received</p>
                  </div>
                  <div className="p-3 bg-indigo-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-indigo-600">{kudosStats.sent_count}</p>
                    <p className="text-xs text-slate-500">Sent</p>
                  </div>
                </div>
                {kudosStats.recent_received?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 mb-2">Recent recognition</p>
                    {kudosStats.recent_received.slice(0, 3).map((k, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm mb-2 p-2 bg-white border rounded-lg">
                        <Award className="h-4 w-4 text-amber-500 flex-shrink-0" />
                        <span className="font-medium">{k.sender_name}</span>
                        <span className="text-slate-400">gave you</span>
                        <Badge variant="outline" className="text-xs">{k.badge_label}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-4">
                <Heart className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-400">Start recognizing your colleagues!</p>
                <Button variant="outline" size="sm" className="mt-2" onClick={() => navigate('/kudos')}>
                  Go to Kudos Wall
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Milestone Badges */}
        {milestones && (
          <Card className="border-l-4 border-l-amber-400" data-testid="milestones-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-500" /> Milestone Badges</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {[
                  { threshold: 5, tier: 'bronze', label: 'Bronze', title: 'Rising Star', color: '#b45309' },
                  { threshold: 10, tier: 'silver', label: 'Silver', title: 'Team Favorite', color: '#64748b' },
                  { threshold: 25, tier: 'gold', label: 'Gold', title: 'Recognition Champion', color: '#ca8a04' },
                  { threshold: 50, tier: 'platinum', label: 'Platinum', title: 'Inspiration Leader', color: '#7c3aed' },
                  { threshold: 100, tier: 'diamond', label: 'Diamond', title: 'Legendary', color: '#2563eb' },
                ].map(ms => {
                  const earned = milestones.earned?.some(e => e.tier === ms.tier);
                  return (
                    <div key={ms.tier}
                      className={`relative flex flex-col items-center p-3 rounded-xl border-2 transition-all min-w-[90px] ${
                        earned
                          ? 'border-current shadow-md scale-105'
                          : 'border-slate-200 opacity-40 grayscale'
                      }`}
                      style={earned ? { borderColor: ms.color, backgroundColor: `${ms.color}10` } : {}}
                      data-testid={`milestone-${ms.tier}`}>
                      <div className="w-10 h-10 rounded-full flex items-center justify-center mb-1"
                        style={{ backgroundColor: earned ? `${ms.color}25` : '#f1f5f9' }}>
                        <Trophy className="h-5 w-5" style={{ color: earned ? ms.color : '#94a3b8' }} />
                      </div>
                      <p className="text-xs font-bold" style={{ color: earned ? ms.color : '#94a3b8' }}>{ms.label}</p>
                      <p className="text-[10px] text-slate-500">{ms.threshold} kudos</p>
                      {earned && <p className="text-[10px] font-medium mt-0.5" style={{ color: ms.color }}>{ms.title}</p>}
                    </div>
                  );
                })}
              </div>
              {milestones.next && (
                <div className="mt-3 p-2 bg-slate-50 rounded-lg flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-indigo-500" />
                  <p className="text-xs text-slate-600">
                    <span className="font-medium">{milestones.next.threshold - milestones.total_received}</span> more kudos to unlock
                    <span className="font-semibold" style={{ color: milestones.next.color }}> {milestones.next.label} ({milestones.next.title})</span>
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* AI Tips Section */}
        <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50/50 to-white" data-testid="ai-tips-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2"><Sparkles className="h-4 w-4 text-indigo-500" /> AI Improvement Tips</CardTitle>
              <Button size="sm" onClick={fetchTips} disabled={tipsLoading} className="bg-indigo-600 hover:bg-indigo-700" data-testid="get-tips-btn">
                {tipsLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Zap className="h-4 w-4 mr-1" />}
                {tips ? 'Refresh Tips' : 'Get Tips'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {tips ? (
              <div className="space-y-4">
                <p className="text-sm text-slate-700 italic">{tips.greeting}</p>
                <p className="text-sm text-slate-600">{tips.overall_assessment}</p>

                {tips.quick_wins?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-indigo-700 uppercase mb-2">Quick Wins</p>
                    {tips.quick_wins.map((qw, i) => (
                      <div key={i} className="flex items-start gap-2 mb-2 p-2 bg-white rounded border">
                        <Zap className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm">{qw.tip}</p>
                          <div className="flex gap-2 mt-1">
                            <Badge variant="outline" className="text-[10px]">{qw.impact} Impact</Badge>
                            <Badge variant="outline" className="text-[10px]">{qw.timeframe}</Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {tips.skill_development?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-indigo-700 uppercase mb-2">Skill Development</p>
                    {tips.skill_development.map((sd, i) => (
                      <div key={i} className="flex items-start gap-2 mb-2 p-2 bg-white rounded border">
                        <BookOpen className="h-4 w-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium">{sd.skill}</p>
                          <p className="text-xs text-slate-600">{sd.suggestion}</p>
                          {sd.resource && <p className="text-xs text-indigo-600 mt-1">Resource: {sd.resource}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {tips.goal_advice && <p className="text-sm text-slate-600 p-2 bg-violet-50 rounded">{tips.goal_advice}</p>}
                {tips.motivation && <p className="text-sm text-indigo-700 font-medium italic text-center">{tips.motivation}</p>}
              </div>
            ) : (
              <div className="text-center py-6">
                <Sparkles className="h-10 w-10 mx-auto text-indigo-300 mb-3" />
                <p className="text-sm text-slate-500">Click "Get Tips" for personalized AI-powered improvement suggestions</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* AI Chat Widget */}
      {!chatOpen ? (
        <button onClick={() => setChatOpen(true)}
          className="fixed bottom-24 right-6 w-14 h-14 bg-indigo-600 hover:bg-indigo-700 rounded-full shadow-xl flex items-center justify-center text-white transition-all hover:scale-110 z-50"
          data-testid="my-perf-chat-toggle">
          <MessageCircle className="h-6 w-6" />
        </button>
      ) : (
        <div className="fixed bottom-6 right-6 w-96 h-[480px] bg-white rounded-2xl shadow-2xl border flex flex-col z-50" data-testid="my-perf-chat-widget">
          <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-indigo-600 to-violet-600 rounded-t-2xl">
            <div className="flex items-center gap-2 text-white">
              <MessageCircle className="h-5 w-5" />
              <span className="font-semibold text-sm">AI Performance Coach</span>
            </div>
            <button onClick={() => setChatOpen(false)} className="text-white/80 hover:text-white"><X className="h-5 w-5" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] p-3 rounded-2xl text-sm ${
                  msg.role === 'user' ? 'bg-indigo-600 text-white rounded-br-sm' : 'bg-slate-100 text-slate-700 rounded-bl-sm'
                }`}>
                  <p className="whitespace-pre-wrap">{msg.text}</p>
                </div>
              </div>
            ))}
            {chatSending && (
              <div className="flex justify-start">
                <div className="bg-slate-100 p-3 rounded-2xl rounded-bl-sm">
                  <div className="flex gap-1"><div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" /><div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{animationDelay:'0.1s'}} /><div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{animationDelay:'0.2s'}} /></div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          <div className="p-3 border-t">
            <div className="flex gap-2">
              <Input value={chatInput} onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendChat()}
                placeholder="Ask about your performance..." className="flex-1" autoComplete="off"
                data-testid="my-perf-chat-input" />
              <Button onClick={sendChat} disabled={chatSending || !chatInput.trim()} size="icon" className="bg-indigo-600 hover:bg-indigo-700" data-testid="my-perf-chat-send">
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {tipsLoading && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 shadow-2xl flex items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            <div><p className="font-semibold">Generating personalized tips...</p><p className="text-sm text-slate-500">Analyzing your data with AI</p></div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default MyPerformanceDashboard;
